import React, { useEffect, useState } from "react";
import axios from "axios";
import { formatBRDate } from "../utils/date";
import { formatLojaName } from "../utils/lodgeName";
import { 
  Users, 
  CalendarCheck, 
  BookOpen, 
  Coins, 
  TrendingUp, 
  HeartHandshake, 
  Flame, 
  ShieldCheck, 
  Sparkles,
  ArrowRight,
  Plus,
  Compass,
  FileText,
  BarChart3,
  Award,
} from "lucide-react";
import { 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
  Area,
  AreaChart
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const MasonicDashboard = ({
  stats,
  members,
  sessions,
  groups,
  currentLoja,
  onNavigateTab,
  onOpenNewMemberModal,
  onOpenNewSessionModal,
  onOpenNewGroupModal,
  onOpenNewFinanceModal,
  canManage = true, // Secretaria/Master. Se false, esconde botões de gestão
}) => {
  const formatCurrency = (val) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(val || 0);
  };

  const degreeColors = {
    "1 - Aprendiz": "#38BDF8",
    "2 - Companheiro": "#A855F7",
    "3 - Mestre": "#D4AF37",
    "Mestre Instalado": "#F59E0B"
  };

  const degreesPieData = Object.entries(stats?.degrees_count || {}).map(([name, value]) => ({
    name,
    value
  }));

  const attendanceBarData = (sessions || []).slice(0, 5).reverse().map((s) => ({
    sessao: `Sessão ${s.session_number}`,
    presentes: s.present_member_ids?.length || 0,
    justificados: s.justified_member_ids?.length || 0,
    tronco: s.trunk_amount || 0
  }));

  const [monthlyFinance, setMonthlyFinance] = useState([]);
  useEffect(() => {
    axios
      .get(`${API}/reports/financial-monthly?months=6`)
      .then((res) => setMonthlyFinance(res.data))
      .catch(() => setMonthlyFinance([]));
  }, [stats]);

  return (
    <div className="space-y-8" data-testid="masonic-dashboard">
      
      {/* Grand Lodge Banner */}
      <div className="relative rounded-2xl overflow-hidden gold-border-glow bg-gradient-to-r from-[#111927] via-[#162238] to-[#0A0E17] p-6 sm:p-8">
        <div className="absolute top-0 right-0 -mt-10 -mr-10 w-72 h-72 rounded-full bg-[#D4AF37]/10 blur-3xl pointer-events-none" />
        
        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="flex items-center space-x-2 text-xs font-semibold uppercase tracking-widest text-[#D4AF37] mb-2">
              <Compass className="w-4 h-4" />
              <span>À Glória do Grande Arquiteto do Universo</span>
            </div>
            <h1 className="text-2xl sm:text-4xl font-serif font-bold text-white tracking-tight" data-testid="dashboard-loja-name">
              {currentLoja
                ? formatLojaName(currentLoja, { withArls: true })
                : "Sua Loja Maçônica"}
            </h1>
            {currentLoja?.oriente && (
              <div className="text-[11px] tracking-widest uppercase text-[#D4AF37]/80 mt-1">
                Oriente de {currentLoja.oriente}
              </div>
            )}
            <p className="text-slate-300 text-sm sm:text-base mt-1 max-w-2xl">
              Plataforma integrada de controle de frequência dos Irmãos, Câmaras de Instrução e gestão da Conta Corrente da Tesouraria e Tronco de Beneficência.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {canManage && (
              <>
                <button
                  data-testid="dashboard-btn-register-member"
                  onClick={onOpenNewMemberModal}
                  className="flex items-center space-x-2 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-4 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all shadow-[0_0_20px_rgba(212,175,55,0.3)]"
                >
                  <Plus className="w-4 h-4" />
                  <span>Cadastrar Irmão</span>
                </button>
                <button
                  data-testid="dashboard-btn-new-session"
                  onClick={onOpenNewSessionModal}
                  className="flex items-center space-x-2 bg-[#131C2E] hover:bg-[#1E293B] text-white border border-[#D4AF37]/40 hover:border-[#D4AF37] font-semibold px-4 py-2.5 rounded-xl text-xs uppercase tracking-wider transition-all"
                >
                  <CalendarCheck className="w-4 h-4 text-[#D4AF37]" />
                  <span>Lançar Sessão</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* 4 Pillars KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        
        {/* Total Members */}
        <div className="luxury-card p-5 group hover:border-[#D4AF37]/60 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Quadro de Obreiros</span>
            <div className="w-10 h-10 rounded-xl bg-blue-500/15 flex items-center justify-center border border-blue-500/30">
              <Users className="w-5 h-5 text-blue-400" />
            </div>
          </div>
          <div className="mt-4">
            <h3 className="text-3xl font-serif font-bold text-white" data-testid="kpi-total-members">
              {stats?.total_members || members.length} Irmãos
            </h3>
            <div className="flex items-center space-x-1.5 mt-2 text-xs text-emerald-400">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>{stats?.regular_members || members.length} Obreiros Regulares</span>
            </div>
          </div>
        </div>

        {/* Global Attendance % */}
        <div className="luxury-card p-5 group hover:border-[#D4AF37]/60 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Frequência da Loja</span>
            <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/15 flex items-center justify-center border border-[#D4AF37]/30">
              <CalendarCheck className="w-5 h-5 text-[#D4AF37]" />
            </div>
          </div>
          <div className="mt-4">
            <h3 className="text-3xl font-serif font-bold text-white font-mono" data-testid="kpi-lodge-frequency">
              {stats?.lodge_frequency_pct || 92.5}%
            </h3>
            <div className="flex items-center space-x-1.5 mt-2 text-xs text-slate-300">
              <span>{stats?.total_sessions || sessions.length} Sessões Litúrgicas realizadas</span>
            </div>
          </div>
        </div>

        {/* Lodge Treasury Checking Account */}
        <div className="luxury-card p-5 group hover:border-[#D4AF37]/60 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Conta Corrente Loja</span>
            <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center border border-emerald-500/30">
              <Coins className="w-5 h-5 text-emerald-400" />
            </div>
          </div>
          <div className="mt-4">
            <h3 className="text-3xl font-serif font-bold text-white font-mono" data-testid="kpi-lodge-balance">
              {formatCurrency(stats?.lodge_cc_balance)}
            </h3>
            <div className="flex items-center space-x-1.5 mt-2 text-xs text-slate-400">
              <span>Soma dos bancos operacionais • Saldo realizado</span>
            </div>
          </div>
        </div>

        {/* Charity Trunk → Bolsa de Beneficência (fundo filantrópico imutável) */}
        <div className="luxury-card p-5 group hover:border-[#D4AF37]/60 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Bolsa de Beneficência</span>
            <div className="w-10 h-10 rounded-xl bg-purple-500/15 flex items-center justify-center border border-purple-500/30">
              <HeartHandshake className="w-5 h-5 text-purple-400" />
            </div>
          </div>
          <div className="mt-4">
            <h3 className="text-3xl font-serif font-bold text-white font-mono" data-testid="kpi-trunk-balance">
              {formatCurrency(stats?.trunk_balance)}
            </h3>
            <div className="flex items-center space-x-1.5 mt-2 text-xs text-purple-300">
              <span>Fundo Filantrópico • Conta Imutável</span>
            </div>
          </div>
        </div>

      </div>

      {/* Visual Charts: Attendance per Session + Degrees Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Attendance Bar Chart */}
        <div className="lg:col-span-2 luxury-card p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-serif font-bold text-white">Histórico de Presenças nas Últimas Sessões</h3>
              <p className="text-xs text-slate-400">Número de Irmãos presentes e justificados por sessão litúrgica</p>
            </div>
            <div className="flex items-center space-x-4 text-xs">
              <div className="flex items-center space-x-1.5">
                <span className="w-3 h-3 rounded-full bg-[#D4AF37]"></span>
                <span className="text-slate-300">Presentes</span>
              </div>
              <div className="flex items-center space-x-1.5">
                <span className="w-3 h-3 rounded-full bg-slate-500"></span>
                <span className="text-slate-300">Justificados</span>
              </div>
            </div>
          </div>

          <div className="h-64 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={attendanceBarData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
                <XAxis dataKey="sessao" stroke="#64748B" fontSize={11} tickLine={false} />
                <YAxis stroke="#64748B" fontSize={11} tickLine={false} allowDecimals={false} />
                <Tooltip 
                  contentStyle={{ backgroundColor: "#131C2E", borderColor: "rgba(212,175,55,0.3)", borderRadius: "8px", color: "#F8FAFC" }}
                />
                <Bar dataKey="presentes" fill="#D4AF37" radius={[4, 4, 0, 0]} />
                <Bar dataKey="justificados" fill="#64748B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Degrees Distribution */}
        <div className="luxury-card p-6 flex flex-col justify-between">
          <div>
            <h3 className="text-lg font-serif font-bold text-white mb-1">Distribuição dos Graus</h3>
            <p className="text-xs text-slate-400 mb-4">Composição do Quadro de Obreiros da Oficina</p>
            
            <div className="space-y-3">
              {degreesPieData.map((d) => (
                <div key={d.name} className="flex items-center justify-between p-2.5 rounded-xl bg-[#080C14] border border-slate-800">
                  <div className="flex items-center space-x-2.5">
                    <span 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: degreeColors[d.name] || "#D4AF37" }}
                    />
                    <span className="text-xs font-semibold text-white">{d.name}</span>
                  </div>
                  <span className="text-xs font-mono font-bold text-[#D4AF37]">{d.value}</span>
                </div>
              ))}
            </div>

            {/* Regra Ouro • Aptos à Progressão */}
            {(() => {
              const aptos = (members || []).filter(m => m.frequency_rule?.apto);
              const almostAptos = (members || []).filter(m => {
                const r = m.frequency_rule;
                return r && !r.apto && r.percent >= 40 && r.percent < 50 && (r.sessions_since || 0) > 0;
              });
              if (aptos.length === 0 && almostAptos.length === 0) return null;
              return (
                <div
                  data-testid="progression-widget"
                  className="mt-5 pt-4 border-t border-slate-800 space-y-2"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Award className="w-4 h-4 text-emerald-400" />
                    <h4 className="text-xs font-serif font-bold text-emerald-300 uppercase tracking-wider">
                      Regra Ouro • Aptos à Progressão
                    </h4>
                  </div>
                  {aptos.length > 0 && (
                    <button
                      type="button"
                      data-testid="widget-aptos-count"
                      onClick={() => onNavigateTab && onNavigateTab("membros")}
                      className="w-full flex items-center justify-between p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/40 hover:bg-emerald-500/20 transition-all group"
                      title="Ver Quadro de Obreiros"
                    >
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-emerald-500/30 flex items-center justify-center text-emerald-300 font-bold text-xs">
                          {aptos.length}
                        </span>
                        <span className="text-xs font-semibold text-emerald-200 text-left">
                          {aptos.length === 1 ? "Irmão APTO" : `Irmãos APTOS`} à próxima Elevação/Exaltação
                        </span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-emerald-400 group-hover:translate-x-1 transition-transform" />
                    </button>
                  )}
                  {almostAptos.length > 0 && (
                    <button
                      type="button"
                      data-testid="widget-almost-count"
                      onClick={() => onNavigateTab && onNavigateTab("membros")}
                      className="w-full flex items-center justify-between p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/40 hover:bg-amber-500/20 transition-all group"
                      title="Irmãos entre 40% e 49% — a uma presença de virar APTO"
                    >
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-amber-500/30 flex items-center justify-center text-amber-300 font-bold text-xs">
                          {almostAptos.length}
                        </span>
                        <span className="text-xs font-semibold text-amber-200 text-left">
                          A uma presença de ficar APTO (≥40% e {"<"}50%)
                        </span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-amber-400 group-hover:translate-x-1 transition-transform" />
                    </button>
                  )}
                  {aptos.length > 0 && (
                    <div className="text-[10px] text-slate-500 italic pt-1">
                      Considere agendar Magna de Elevação ou Cerimônia de Exaltação para os Irmãos aptos.
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          <div className="mt-6 pt-4 border-t border-slate-800 flex items-center justify-between">
            <span className="text-xs text-slate-400">Câmaras de Instrução:</span>
            <span className="text-xs font-bold text-[#D4AF37]">{groups.length} Grupos Ativos</span>
          </div>
        </div>

      </div>

      {/* Monthly Financial Trend - New Feature */}
      <div className="luxury-card p-6" data-testid="dashboard-financial-trend">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="flex items-center space-x-2 text-xs uppercase tracking-wider text-emerald-400 font-bold mb-1">
              <BarChart3 className="w-3.5 h-3.5" />
              <span>Tesouraria • Últimos 6 meses</span>
            </div>
            <h3 className="text-lg font-serif font-bold text-white">Evolução Financeira da Loja</h3>
            <p className="text-xs text-slate-400">Movimentação da Conta Corrente e do Tronco de Beneficência</p>
          </div>
          <div className="flex items-center space-x-4 text-xs">
            <div className="flex items-center space-x-1.5">
              <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
              <span className="text-slate-300">Receitas</span>
            </div>
            <div className="flex items-center space-x-1.5">
              <span className="w-3 h-3 rounded-full bg-rose-500"></span>
              <span className="text-slate-300">Despesas</span>
            </div>
            <div className="flex items-center space-x-1.5">
              <span className="w-3 h-3 rounded-full bg-purple-500"></span>
              <span className="text-slate-300">Tronco</span>
            </div>
          </div>
        </div>

        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <AreaChart data={monthlyFinance} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorReceita" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.5} />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorDespesa" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#F43F5E" stopOpacity={0.5} />
                  <stop offset="95%" stopColor="#F43F5E" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorTronco" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#A855F7" stopOpacity={0.5} />
                  <stop offset="95%" stopColor="#A855F7" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
              <XAxis dataKey="label" stroke="#64748B" fontSize={11} tickLine={false} />
              <YAxis
                stroke="#64748B"
                fontSize={11}
                tickLine={false}
                tickFormatter={(v) => `R$${(v / 1000).toFixed(1)}k`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#131C2E",
                  borderColor: "rgba(212,175,55,0.3)",
                  borderRadius: "8px",
                  color: "#F8FAFC",
                }}
                formatter={(val, name) => [formatCurrency(val), name]}
              />
              <Area
                type="monotone"
                dataKey="receita"
                stroke="#10B981"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorReceita)"
                name="Receitas"
              />
              <Area
                type="monotone"
                dataKey="despesa"
                stroke="#F43F5E"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorDespesa)"
                name="Despesas"
              />
              <Area
                type="monotone"
                dataKey="tronco_receita"
                stroke="#A855F7"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorTronco)"
                name="Tronco Beneficência"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Sessions & Quick Access */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Recent Sessions */}
        <div className="luxury-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-serif font-bold text-white">Sessões Recentes da Loja</h3>
              <p className="text-xs text-slate-400">Atas e registros de presença</p>
            </div>
            <button
              onClick={() => onNavigateTab("attendance")}
              className="text-xs text-[#D4AF37] hover:underline font-semibold flex items-center space-x-1"
            >
              <span>Ver Todas</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="space-y-3">
            {(stats?.recent_sessions || []).map((s) => (
              <div key={s.id} className="p-3.5 rounded-xl bg-[#080C14] border border-slate-800 flex items-center justify-between">
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="font-mono text-xs font-bold text-[#D4AF37]">Nº {s.session_number}</span>
                    <span className="text-xs text-slate-400">({formatBRDate(s.date)})</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/30">
                      {s.session_type}
                    </span>
                  </div>
                  <h4 className="text-sm font-semibold text-white mt-1">{s.title}</h4>
                </div>

                <div className="text-right text-xs">
                  <span className="font-bold text-emerald-400 block">{s.present_member_ids?.length || 0} Presentes</span>
                  <span className="text-slate-400 font-mono text-[11px]">Tronco: {formatCurrency(s.trunk_amount)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Study Groups & Instruction Programs Preview */}
        <div className="luxury-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-serif font-bold text-white">Câmaras de Instrução & Comissões</h3>
              <p className="text-xs text-slate-400">Programas de estudos e tarefas litúrgicas</p>
            </div>
            <button
              onClick={() => onNavigateTab("groups")}
              className="text-xs text-[#D4AF37] hover:underline font-semibold flex items-center space-x-1"
            >
              <span>Ver Grupos</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="space-y-3">
            {(groups || []).slice(0, 3).map((g) => (
              <div key={g.id} className="p-3.5 rounded-xl bg-[#080C14] border border-slate-800 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-white">{g.name}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30">
                    {g.degree_target}
                  </span>
                </div>
                <p className="text-xs text-slate-400 line-clamp-1">{g.description}</p>
                <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1 border-t border-slate-800/60">
                  <span>Coord: {g.coordinator_name}</span>
                  <span>{g.topics_or_tasks?.length || 0} temas cadastrados</span>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>

    </div>
  );
};
